import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSiteAccess } from '../../app/SiteAccessContext.jsx';
import { api } from '../../lib/apiClient.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { toast } from '../../lib/useToast.js';
import {
  EmptyState,
  ErrorState,
  LoadingSkeleton,
  PageHeader,
  PageViewerControls,
  StatusChip,
} from '../../components/index.js';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { AddEdipnDialog } from './AddEdipnDialog.jsx';
import { SiteAccessDecisionPanel } from './SiteAccessDecisionPanel.jsx';
import { SiteAccessQueue } from './SiteAccessQueue.jsx';
import './SiteAccessAdmin.css';

const ENFORCEMENT_PHRASE = 'ENFORCE SITE ACCESS';
const DEFAULT_LIMIT = 25;
const VALID_TABS = new Set(['pending', 'roster', 'inactive']);

const ACTION_COPY = {
  approve: {
    title: 'Approve access?',
    body: 'Approving lets this EDIPN pass the site gate and reach the persona login. This is an audited, reversible action.',
    confirmLabel: 'Approve access',
    tone: 'primary',
    requireNote: false,
    showNote: true,
    noteLabel: 'Decision note (optional)',
    noteHint: 'Optional — recorded with the decision when provided.',
    notePlaceholder: 'Capture the basis for this decision.',
    success: (identity) => `Access approved — ${identity.edipn} can now reach PMHub.`,
    errorAction: 'approve',
  },
  deny: {
    title: 'Deny access?',
    body: 'Denying is a terminal decision — the requester must file a new request to regain access.',
    confirmLabel: 'Deny',
    tone: 'danger',
    requireNote: true,
    noteLabel: 'Decision note',
    notePlaceholder: 'Document why this request is being denied.',
    success: (identity) => `Access denied — ${identity.edipn} cannot reach PMHub.`,
    errorAction: 'deny',
  },
  suspend: {
    title: 'Suspend access?',
    body: 'Suspending immediately pauses this EDIPN at the site gate. This is audited and can be restored later.',
    confirmLabel: 'Suspend',
    tone: 'danger',
    requireNote: false,
    showNote: true,
    noteLabel: 'Decision note (optional)',
    noteHint: 'Optional — recorded with the decision when provided.',
    notePlaceholder: 'Capture why access is being suspended.',
    success: (identity) => `Access suspended — ${identity.edipn} is blocked from the site.`,
    errorAction: 'suspend',
  },
  restore: {
    title: 'Restore access?',
    body: 'Restoring moves this EDIPN back to approved so the holder can reach the persona login again.',
    confirmLabel: 'Restore',
    tone: 'primary',
    requireNote: false,
    showNote: true,
    noteLabel: 'Decision note (optional)',
    noteHint: 'Optional — recorded with the decision when provided.',
    notePlaceholder: 'Capture why access is being restored.',
    success: (identity) => `Access restored — ${identity.edipn} can reach PMHub again.`,
    errorAction: 'restore',
  },
  revoke: {
    title: 'Revoke access?',
    body: "Revoking immediately blocks this EDIPN from the site. Terminal and audited — they'd have to request access again.",
    confirmLabel: 'Revoke',
    tone: 'danger',
    requireNote: true,
    noteLabel: 'Decision note',
    notePlaceholder: 'Document why this access is being revoked.',
    success: (identity) => `Access revoked — ${identity.edipn} must request access again.`,
    errorAction: 'revoke',
  },
  grantApprover: {
    title: 'Make this EDIPN an approver?',
    body: 'Approvers can manage this site-access list. They cannot remove the owner or change enforcement.',
    confirmLabel: 'Make approver',
    tone: 'primary',
    requireNote: false,
    showNote: true,
    noteLabel: 'Decision note (optional)',
    noteHint: 'Optional — recorded with the decision when provided.',
    notePlaceholder: 'Capture the basis for elevating this approver.',
    success: (identity) => `${identity.edipn} can now manage site access.`,
    errorAction: 'make approver',
  },
  removeApprover: {
    title: 'Remove approver rights?',
    body: 'This EDIPN will keep site access but lose delegated approver rights for the console.',
    confirmLabel: 'Remove approver',
    tone: 'danger',
    requireNote: false,
    showNote: true,
    noteLabel: 'Decision note (optional)',
    noteHint: 'Optional — recorded with the decision when provided.',
    notePlaceholder: 'Capture why approver rights are being removed.',
    success: (identity) => `Approver rights removed for ${identity.edipn}.`,
    errorAction: 'remove approver',
  },
};

function normalizeTab(value) {
  return VALID_TABS.has(value) ? value : 'pending';
}

function tabToStatus(tab) {
  return tab === 'roster' ? 'approved' : tab;
}

function buildParams(searchParams, next) {
  const updated = new URLSearchParams(searchParams);
  Object.entries(next).forEach(([key, value]) => {
    if (value == null || value === '') updated.delete(key);
    else updated.set(key, value);
  });
  return updated;
}

function actionMatrix(identity) {
  if (!identity) return [];
  if (identity.status === 'pending') return ['approve', 'deny', 'grantApprover'];
  if (identity.status === 'approved') return ['suspend', 'revoke', 'grantApprover', 'removeApprover'];
  if (identity.status === 'suspended') return ['restore', 'revoke'];
  return [];
}

function LockedState() {
  return (
    <section className="access-locked site-access-admin__locked" data-testid="nonapprover-locked">
      <span className="eyebrow">Site access control</span>
      <h2>You&apos;re signed in, but only the site owner and delegated approvers can manage site access.</h2>
      <p>Use the current route URL when you need to verify your access posture, but this console stays locked until an approver grants access.</p>
    </section>
  );
}

function KpiTile({ testId, value, label, tone = 'slate', highlight = false }) {
  return (
    <article
      className={`site-access-admin__kpi${highlight ? ' is-highlight' : ''}`}
      data-testid={testId}
    >
      <div className="site-access-admin__kpi-value mono">{value}</div>
      <div className={`site-access-admin__kpi-label tone-${tone}`}>{label}</div>
    </article>
  );
}

function RecentActivityStrip({ items }) {
  return (
    <section className="card compact site-access-admin__activity" data-testid="audit-strip">
      <div className="site-access-admin__section-head">
        <h2>Recent site-access activity</h2>
      </div>
      {items.length ? (
        <div className="site-access-admin__activity-list">
          {items.map((item) => (
            <article key={item.id} className="site-access-admin__activity-item" data-testid={`audit-item-${item.id}`}>
              <div>
                <div className="site-access-admin__activity-summary">
                  {item.summary || item.action || item.targetId}
                </div>
                <div className="site-access-admin__activity-meta mono">
                  {item.actorUserId || '—'}
                </div>
              </div>
              <time className="site-access-admin__activity-time">
                {item.occurredAt ? new Date(item.occurredAt).toLocaleString() : '—'}
              </time>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No recent site-access activity" description="Recent site-access audits will appear here after the first decision is recorded." />
      )}
    </section>
  );
}

export function SiteAccessAdminPage() {
  const { loading: siteLoading, state: siteState, refresh: refreshSiteAccess } = useSiteAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchDraft, setSearchDraft] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [noteDraft, setNoteDraft] = useState('');
  const [pendingSelection, setPendingSelection] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({ kind: null, action: null, target: null });
  const decisionPanelRef = useRef(null);

  const activeTab = normalizeTab(searchParams.get('tab'));
  const selectedEdipn = searchParams.get('edipn');
  const adminConsoleEnabled = siteState?.adminConsoleEnabled !== false;
  const isApprover = Boolean(siteState?.isApprover || siteState?.isOwner);
  const isOwner = Boolean(siteState?.isOwner);
  const isOutage = siteState?.directoryState === 'unavailable';
  const lockedBySiteAccess = !siteLoading && !isApprover;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchDraft.trim());
    }, 200);
    return () => window.clearTimeout(handle);
  }, [searchDraft]);

  useEffect(() => {
    setOffset(0);
  }, [activeTab, debouncedSearch]);

  const listRes = useApi('/admin/site-access', {
    query: {
      status: tabToStatus(activeTab),
      search: debouncedSearch || undefined,
      limit: DEFAULT_LIMIT,
      offset,
    },
    deps: [activeTab, debouncedSearch, offset],
    skip: lockedBySiteAccess || !adminConsoleEnabled,
  });

  const recentRes = useApi('/admin/site-access/recent-activity', {
    query: { limit: 10 },
    skip: lockedBySiteAccess || !adminConsoleEnabled,
  });

  const enforcementRes = useApi('/admin/site-access/enforcement', {
    skip: lockedBySiteAccess || !adminConsoleEnabled || !isOwner,
  });

  const currentRows = listRes.data?.data || [];

  const detailRes = useApi(selectedEdipn ? `/admin/site-access/${selectedEdipn}` : null, {
    skip: lockedBySiteAccess || !adminConsoleEnabled || !selectedEdipn,
  });

  const admin403 = [listRes.error, detailRes.error, recentRes.error]
    .filter(Boolean)
    .some((error) => error?.status === 403);

  useEffect(() => {
    if (lockedBySiteAccess || admin403) return;
    if (listRes.loading) return;
    const hasSelected = selectedEdipn && currentRows.some((item) => item.edipn === selectedEdipn);
    if (hasSelected) return;
    if (currentRows.length === 0) {
      if (selectedEdipn) {
        setSearchParams(buildParams(searchParams, { edipn: null, tab: activeTab }), {
          replace: true,
        });
      }
      return;
    }
    const nextEdipn = currentRows[0].edipn;
    setSearchParams(
      buildParams(searchParams, { tab: activeTab, edipn: nextEdipn }),
      { replace: true },
    );
  }, [
    activeTab,
    admin403,
    currentRows,
    listRes.loading,
    lockedBySiteAccess,
    searchParams,
    selectedEdipn,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!selectedEdipn || window.innerWidth >= 1080) return;
    decisionPanelRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [selectedEdipn]);

  const selectedIdentity = selectedEdipn
    ? detailRes.data?.data || currentRows.find((item) => item.edipn === selectedEdipn) || null
    : null;

  const mutationDisabled = isOutage;
  const counts = listRes.data?.meta?.summaryCounts || {
    pending: 0,
    approved: 0,
    inactive: 0,
    approvers: 0,
  };
  const actionMutation = useMutation();
  const enforcementMutation = useMutation();
  const addMutation = useMutation();

  const enforced = enforcementRes.data?.allowlistEnforced ?? Boolean(siteState?.allowlistEnforced);
  const envForced = Boolean(enforcementRes.data?.envForced);
  const bannerMode = siteState?.gateMode === 'cac-disabled' ? 'cac-disabled' : enforced ? 'enforced' : 'monitoring';

  const confirmDialogProps = useMemo(() => {
    if (confirmState.kind === 'discard') {
      return {
        title: 'Discard this note draft?',
        body: 'Switching rows will discard the unsaved decision note you have typed.',
        confirmLabel: 'Discard note',
        cancelLabel: 'Keep editing',
        tone: 'danger',
        showNote: false,
        requireNote: false,
        confirmEnabled: true,
      };
    }
  if (confirmState.kind === 'enforcement') {
      return {
        title: enforced ? 'Change enforcement mode?' : 'Turn enforcement on?',
        body: `Type ${ENFORCEMENT_PHRASE} to confirm this site-access enforcement change.`,
        confirmLabel: enforced ? 'Apply change' : 'Turn enforcement on',
        cancelLabel: 'Cancel',
        tone: 'danger',
        showNote: true,
        requireNote: false,
        noteLabel: 'Confirmation phrase',
        noteHint: 'The server validates one fixed phrase for both directions.',
        notePlaceholder: ENFORCEMENT_PHRASE,
        confirmEnabled: true,
      };
    }
    if (confirmState.kind === 'action' && confirmState.action) {
      return ACTION_COPY[confirmState.action];
    }
    return null;
  }, [confirmState, enforced]);

  const handleSelection = (nextEdipn) => {
    if (!nextEdipn || nextEdipn === selectedEdipn) return;
    if (noteDraft.trim()) {
      setPendingSelection(nextEdipn);
      setConfirmState({ kind: 'discard', action: null, target: null });
      return;
    }
    setSearchParams(buildParams(searchParams, { tab: activeTab, edipn: nextEdipn }), {
      replace: true,
    });
  };

  const openActionConfirm = (action) => {
    if (!selectedIdentity || mutationDisabled) return;
    setConfirmState({ kind: 'action', action, target: selectedIdentity });
  };

  const openEnforcementConfirm = () => {
    if (!isOwner || mutationDisabled || envForced) return;
    setConfirmState({ kind: 'enforcement', action: null, target: null });
  };

  const handleConfirm = async (note) => {
    if (confirmState.kind === 'discard') {
      const nextEdipn = pendingSelection;
      setPendingSelection(null);
      setNoteDraft('');
      setConfirmState({ kind: null, action: null, target: null });
      if (nextEdipn) {
        setSearchParams(buildParams(searchParams, { tab: activeTab, edipn: nextEdipn }), {
          replace: true,
        });
      }
      return;
    }

    if (confirmState.kind === 'enforcement') {
      try {
        await enforcementMutation.run(() =>
          api.put('/admin/site-access/enforcement', {
            allowlistEnforced: !enforced,
            confirmationText: note,
          }),
        );
        toast.success({
          title: 'Enforcement updated',
          body: !enforced
            ? 'Site access enforcement is now fail-closed.'
            : 'Site access enforcement is now in monitoring mode.',
        });
        setConfirmState({ kind: null, action: null, target: null });
        enforcementRes.refresh();
        refreshSiteAccess?.();
      } catch (error) {
        toast.error({
          title: 'Enforcement update failed',
          body: error.message,
        });
      }
      return;
    }

    if (confirmState.kind === 'action' && confirmState.action && confirmState.target) {
      const target = confirmState.target;
      try {
        await actionMutation.run(() =>
          api.post(`/admin/site-access/${target.edipn}/actions`, {
            action: confirmState.action,
            note: note || undefined,
          }),
        );
        toast.success({
          title: 'Decision recorded',
          body: ACTION_COPY[confirmState.action].success(target),
        });
        setNoteDraft('');
        setConfirmState({ kind: null, action: null, target: null });
        listRes.refresh();
        detailRes.refresh?.();
        recentRes.refresh();
      } catch (error) {
        toast.error({
          title: `${ACTION_COPY[confirmState.action].confirmLabel} failed`,
          body: `Couldn't ${ACTION_COPY[confirmState.action].errorAction} — ${error.message}. Nothing changed.`,
        });
      }
    }
  };

  const handleAddEdipn = async (payload) => {
    try {
      const response = await addMutation.run(() => api.post('/admin/site-access', payload));
      const nextStatus = response?.data?.status || payload.status || 'approved';
      const nextTab = nextStatus === 'pending' ? 'pending' : nextStatus === 'approved' ? 'roster' : 'inactive';
      toast.success({
        title: 'Site access updated',
        body:
          nextStatus === 'pending'
            ? `${payload.edipn} was added to the pending queue.`
            : `${payload.edipn} was added to the site-access roster.`,
      });
      setAddDialogOpen(false);
      setSearchDraft('');
      setDebouncedSearch('');
      setOffset(0);
      setSearchParams(buildParams(searchParams, { tab: nextTab, edipn: payload.edipn }), {
        replace: true,
      });
      listRes.refresh();
      recentRes.refresh();
    } catch (error) {
      toast.error({
        title: 'Add EDIPN failed',
        body: error.message,
      });
    }
  };

  if (siteLoading) {
    return (
      <div className="stack">
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (!adminConsoleEnabled) {
    return (
      <section className="access-locked site-access-admin__locked" data-testid="site-access-local-only">
        <EmptyState
          title="Site Access is not enabled for this session"
          description="This console is available only in approved site-access admin sessions."
        />
      </section>
    );
  }

  if (lockedBySiteAccess || admin403) {
    return <LockedState />;
  }

  if (listRes.error && listRes.error.status !== 403) {
    return <ErrorState error={listRes.error} onRetry={listRes.refresh} />;
  }

  const bannerBody =
    bannerMode === 'enforced'
      ? 'Enforcement is ON — fail-closed. Only approved EDIPNs (and the pinned owner) can reach the app.'
      : bannerMode === 'monitoring'
        ? 'Monitoring only — no one is blocked yet. Add approved EDIPNs, then turn enforcement on.'
        : 'CAC is disabled for this environment. The console reflects site-access records without blocking local access.';

  return (
    <div className="site-access-admin" data-testid="site-access-page">
      <PageHeader
        eyebrow="Site access control"
        title="Who can reach PMHub"
        subtitle="Approve, deny, suspend, or revoke individual CAC holders by EDIPN. Card holders who are not on this list cannot pass the site gate."
        actions={
          <div className="site-access-admin__header-actions">
            <div className="site-access-admin__identity-pill" data-testid="cac-context-pill">
              <span className="site-access-admin__identity-label">You:</span>
              <span className="mono">{siteState?.edipn || '—'}</span>
              {isOwner ? <span className="chip blue sm">Owner</span> : null}
              {siteState?.isApprover ? <span className="chip outline sm">Approver</span> : null}
            </div>
            <button
              type="button"
              className="btn ghost"
              data-testid="enforcement-toggle"
              disabled={!isOwner || mutationDisabled || envForced}
              onClick={openEnforcementConfirm}
              title={
                envForced
                  ? 'Server configuration pins enforcement in this environment.'
                  : !isOwner
                    ? 'Only the root owner can change enforcement mode.'
                    : undefined
              }
            >
              Enforcement: {bannerMode === 'enforced' ? 'Enforced' : bannerMode === 'monitoring' ? 'Monitoring' : 'CAC disabled'}
            </button>
            {!isOwner ? (
              <div className="site-access-admin__assistive-copy">
                Only the root owner can change enforcement mode.
              </div>
            ) : null}
            {envForced ? (
              <div className="site-access-admin__assistive-copy">
                Enforcement is pinned by server configuration in this environment.
              </div>
            ) : null}
          </div>
        }
      />

      <section className={`banner ${bannerMode === 'enforced' ? 'green' : bannerMode === 'monitoring' ? 'blue' : 'amber'} site-access-admin__banner`} data-testid="enforcement-banner">
        <div className="stack tight">
          <strong>{bannerBody}</strong>
          {isOutage ? (
            <span className="site-access-admin__assistive-copy">
              The access directory is temporarily unavailable — changes are paused.
            </span>
          ) : null}
        </div>
        <span className={`chip ${bannerMode === 'enforced' ? 'green' : bannerMode === 'monitoring' ? 'blue' : 'amber'}`} data-testid="enforcement-status">
          {bannerMode === 'enforced' ? 'Enforced' : bannerMode === 'monitoring' ? 'Monitoring' : 'CAC disabled'}
        </span>
      </section>

      <section className="site-access-admin__kpi-band" data-testid="kpi-band">
        <KpiTile testId="kpi-pending" value={counts.pending} label="Pending requests" tone="amber" highlight={counts.pending > 0} />
        <KpiTile testId="kpi-approved" value={counts.approved} label="Approved" tone="sage" />
        <KpiTile testId="kpi-suspended" value={counts.inactive} label="Suspended / revoked" tone="slate" />
        <KpiTile testId="kpi-approvers" value={counts.approvers} label="Approvers" tone="blue" />
      </section>

      <div className="site-access-admin__cockpit">
        <SiteAccessQueue
          activeTab={activeTab}
          counts={counts}
          items={currentRows}
          loading={listRes.loading}
          searchValue={searchDraft}
          selectedEdipn={selectedEdipn}
          onAddEdipn={() => setAddDialogOpen(true)}
          onSearchChange={setSearchDraft}
          onSelect={handleSelection}
          onTabChange={(nextTab) =>
            setSearchParams(buildParams(searchParams, { tab: nextTab, edipn: null }), {
              replace: true,
            })
          }
          addDisabled={mutationDisabled}
        />

        <div ref={decisionPanelRef}>
          {detailRes.error && detailRes.error.status !== 403 ? (
            <ErrorState error={detailRes.error} onRetry={detailRes.refresh} />
          ) : (
            <SiteAccessDecisionPanel
              identity={selectedIdentity}
              noteDraft={noteDraft}
              onNoteChange={setNoteDraft}
              onAction={openActionConfirm}
              mutationDisabled={mutationDisabled}
              isOwnerViewer={isOwner}
              isDelegateViewer={!isOwner && isApprover}
            />
          )}
        </div>
      </div>

      <PageViewerControls
        page={listRes.data?.page}
        label="site access identities"
        pageSize={DEFAULT_LIMIT}
        onOffsetChange={setOffset}
      />

      <RecentActivityStrip items={recentRes.data?.data || []} />

      <AddEdipnDialog
        open={addDialogOpen}
        busy={addMutation.running}
        onClose={() => setAddDialogOpen(false)}
        onSubmit={handleAddEdipn}
      />

      {confirmDialogProps ? (
        <ConfirmActionDialog
          open
          title={confirmDialogProps.title}
          body={confirmDialogProps.body}
          confirmLabel={confirmDialogProps.confirmLabel}
          cancelLabel={confirmDialogProps.cancelLabel}
          tone={confirmDialogProps.tone}
          requireNote={confirmDialogProps.requireNote}
          showNote={confirmDialogProps.showNote}
          noteLabel={confirmDialogProps.noteLabel}
          noteHint={confirmDialogProps.noteHint}
          notePlaceholder={confirmDialogProps.notePlaceholder}
          busy={actionMutation.running || enforcementMutation.running}
          confirmEnabled={
            confirmState.kind === 'enforcement'
              ? true
              : confirmDialogProps.confirmEnabled
          }
          requireExactText={
            confirmState.kind === 'enforcement' ? ENFORCEMENT_PHRASE : null
          }
          onConfirm={handleConfirm}
          onCancel={() => {
            setPendingSelection(null);
            setConfirmState({ kind: null, action: null, target: null });
          }}
        />
      ) : null}
    </div>
  );
}
